/** Hard cap so a forgotten mic session cannot run indefinitely. */
export const DICTATION_MAX_SESSION_MS = 10 * 60 * 1000
export const DICTATION_MAX_SESSION_SECONDS = DICTATION_MAX_SESSION_MS / 1000

/** Temp keys only need to *open* the WebSocket; an open session stays up. */
export const DICTATION_TEMP_KEY_TTL_SECONDS = 60

export type DictationToken = {
  text: string
  is_final: boolean
}

/** Append a transcript into the composer, inserting a space when needed. */
export function appendDictationTranscript(currentValue: string, transcript: string): string {
  const trimmed = transcript.trim()
  if (!trimmed) return currentValue
  if (!currentValue) return trimmed
  if (/\s$/.test(currentValue)) return `${currentValue}${trimmed}`
  return `${currentValue} ${trimmed}`
}

/** Split one Soniox partial into newly committed finals vs the current hypothesis. */
export function splitDictationTokens(tokens: readonly DictationToken[]): {
  finalText: string
  nonFinalText: string
} {
  let finalText = ""
  let nonFinalText = ""
  for (const token of tokens) {
    if (token.is_final) finalText += token.text
    else nonFinalText += token.text
  }
  return { finalText, nonFinalText }
}

export function composeLiveDictationValue(committedTranscript: string, nonFinalText: string): string {
  return `${committedTranscript}${nonFinalText}`
}

/**
 * Soniox `result` events are incremental (the SDK appends incoming tokens), but
 * some payloads repeat already-final text as a growing snapshot. Keep prior
 * finals when a result has only non-finals or is empty.
 */
export function mergeDictationTranscript(
  previousCommitted: string,
  tokens: readonly DictationToken[],
): { committedText: string; nonFinalText: string } {
  const { finalText, nonFinalText } = splitDictationTokens(tokens)
  if (!finalText) return { committedText: previousCommitted, nonFinalText }
  if (!previousCommitted || finalText.startsWith(previousCommitted)) {
    return { committedText: finalText, nonFinalText }
  }
  return { committedText: `${previousCommitted}${finalText}`, nonFinalText }
}
